/**
 * v109.9.12 — Travel Advance v2 form
 * ----------------------------------
 * Enhanced form used by the Travel module to raise a new advance request.
 *
 * Features:
 *   • Purpose (Sale/Demo/Training/Support/Meet/Other + free-text remark)
 *   • From & To date-time with auto-computed "Days" count
 *   • CRM Client picker (existing clients) OR Bypass toggle with warning
 *   • Transport modes multi-select (Own Vehicle / Public Transport / Company Vehicle)
 *     with dynamic cost fields (KMS, Fuel, Toll, Public Transport)
 *   • Lodging + Food costs, live total + Advance requested
 *   • Live "who will approve" preview (2-tier vs 4-tier) based on threshold + bypass
 *   • Unsettled amount warning if user has advances not yet settled
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { emitDataChange } from "../lib/events";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { AlertTriangle, Users, ShieldCheck, Calculator, Info } from "lucide-react";

const PURPOSE_OPTIONS = ["Sale", "Demo", "Training", "Support", "Meet", "Other"];
const TRANSPORT_MODES = [
  { key: "Own Vehicle",       label: "Own Vehicle" },
  { key: "Public Transport",  label: "Public Transport" },
  { key: "Company Vehicle",   label: "Company Vehicle" },
];

function computeDays(from, to) {
  if (!from || !to) return 0;
  const a = new Date(from), b = new Date(to);
  if (isNaN(a) || isNaN(b) || b < a) return 0;
  const secs = (b - a) / 1000;
  return Math.max(1, Math.ceil(secs / 86400));
}

function num(v) {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export default function TravelAdvanceFormDialog({ open, onClose, onCreated, editRecord = null, prefill = null }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const isEdit = !!editRecord;

  // Preload config + clients + unsettled summary
  const [cfg, setCfg] = useState({ pool_users: [], amount_threshold: 25000 });
  const [clients, setClients] = useState([]);
  const [unsettled, setUnsettled] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        // v109.9.165 — Client dropdown now honours SA-ownership +
        // trainer-assigned clients via `/travel/eligible-clients`.
        // Falls back to full CRM list only if the eligible endpoint
        // returns empty (e.g. CRM admins with no self-owned clients).
        const [{ data: c }, { data: cli }, { data: cliAll }, { data: u }] = await Promise.all([
          api.get("/travel-advances/config"),
          api.get("/travel/eligible-clients").catch(() => ({ data: [] })),
          api.get("/records/crm_clients").catch(() => ({ data: [] })),
          api.get("/travel-advances/unsettled-summary?scope=self").catch(() => ({ data: [] })),
        ]);
        setCfg(c || {});
        const eligibleList = Array.isArray(cli) && cli.length > 0 ? cli : (Array.isArray(cliAll) ? cliAll : []);
        setClients(eligibleList);
        setUnsettled((u || [])[0] || null);
      } catch {
        /* swallow — form will fall back to defaults */
      }
    })();
  }, [open]);

  // Form state
  const [form, setForm] = useState({
    travel_purpose: "",
    purpose_other: "",
    from_datetime: "",
    to_datetime: "",
    destination: "",
    // v109.9.110 — Multi-client picker. Retained legacy `client_id` /
    // `client_name` fields on the payload (populated from the first
    // selection + joined names) so downstream dashboards keep working.
    client_ids: [],
    client_names: [],
    bypass_crm: false,
    transport_modes: [],
    kms: "",
    fuel_cost: "",
    toll_cost: "",
    public_transport_cost: "",
    lodging_cost: "",
    food_cost: "",
    advance_amount: "",
    remark: "",
  });

  useEffect(() => {
    if (!open) return;
    if (editRecord) {
      // Pre-fill from existing record. Purpose may be stored as "Other — text".
      const rawPurpose = editRecord.travel_purpose || "";
      let purposeKey = rawPurpose;
      let purposeOther = editRecord.purpose_other || "";
      if (rawPurpose.startsWith("Other")) {
        purposeKey = "Other";
        if (!purposeOther && rawPurpose.includes("—")) {
          purposeOther = rawPurpose.split("—").slice(1).join("—").trim();
        }
      }
      setForm({
        travel_purpose: purposeKey,
        purpose_other: purposeOther,
        from_datetime: (editRecord.from_datetime || "").slice(0, 16),
        to_datetime: (editRecord.to_datetime || "").slice(0, 16),
        destination: editRecord.destination || "",
        client_ids: editRecord.client_ids && editRecord.client_ids.length
          ? editRecord.client_ids
          : (editRecord.client_id ? [editRecord.client_id] : []),
        client_names: editRecord.client_names && editRecord.client_names.length
          ? editRecord.client_names
          : (editRecord.client_name ? [editRecord.client_name] : []),
        bypass_crm: !!editRecord.bypass_crm,
        transport_modes: editRecord.transport_modes || [],
        kms: String(editRecord.kms ?? ""),
        fuel_cost: String(editRecord.fuel_cost ?? ""),
        toll_cost: String(editRecord.toll_cost ?? ""),
        public_transport_cost: String(editRecord.public_transport_cost ?? ""),
        lodging_cost: String(editRecord.lodging_cost ?? ""),
        food_cost: String(editRecord.food_cost ?? ""),
        advance_amount: String(editRecord.advance_amount ?? ""),
        remark: editRecord.remark || "",
      });
      return;
    }
    setForm({
      travel_purpose: prefill?.purpose_of_travel ? "Other" : "",
      purpose_other: prefill?.purpose_of_travel || "",
      from_datetime: prefill?.start_date ? `${prefill.start_date}T09:00` : "",
      to_datetime: prefill?.end_date ? `${prefill.end_date}T18:00` : "",
      destination: prefill?.destination || "",
      client_ids: prefill?.client_ids || [],
      client_names: prefill?.client_names || [],
      bypass_crm: false,
      transport_modes: [], kms: "", fuel_cost: "", toll_cost: "",
      public_transport_cost: "", lodging_cost: "", food_cost: "",
      advance_amount: "", remark: prefill?.trainer_assignment_id ? `Linked to trainer assignment ${prefill.trainer_assignment_id}` : "",
      trainer_assignment_id: prefill?.trainer_assignment_id || "",
    });
  }, [open, editRecord, prefill]);

  const days = useMemo(() => computeDays(form.from_datetime, form.to_datetime), [form.from_datetime, form.to_datetime]);
  const totalBreakdown = useMemo(
    () => num(form.fuel_cost) + num(form.toll_cost) + num(form.public_transport_cost) + num(form.lodging_cost) + num(form.food_cost),
    [form.fuel_cost, form.toll_cost, form.public_transport_cost, form.lodging_cost, form.food_cost],
  );
  const advanceAmount = num(form.advance_amount) || totalBreakdown;

  const hasOwn = form.transport_modes.includes("Own Vehicle");
  const hasPublic = form.transport_modes.includes("Public Transport");

  const routing = useMemo(() => {
    const th = num(cfg?.amount_threshold) || 25000;
    // v109.9.167 — When editing an existing advance, freeze the approval
    // flow preview to the record's original routing. Prevents MA edits from
    // visually re-routing the request just because the amount changed.
    if (isEdit && editRecord) {
      const origApprovers = (editRecord.approvals || []).map((a) => ({
        name: a.name || a.role || "",
        tag: a.role || a.tag || "Approver",
      })).filter((a) => a.name);
      return {
        type: editRecord.approval_type || (origApprovers.length > 2 ? "4-tier" : "2-tier"),
        threshold: th,
        approvers: origApprovers,
        pool_configured: true,
        locked: true,
      };
    }
    const fourTier = form.bypass_crm || advanceAmount >= th;
    const rmName = user?.reporting_manager_name || "";
    const pool = cfg?.pool_users || [];
    return {
      type: fourTier ? "4-tier" : "2-tier",
      threshold: th,
      approvers: fourTier
        ? pool.map((p) => ({ name: p.name, tag: "Pool Approver" }))
        : [
            ...(rmName ? [{ name: rmName, tag: "Reporting Manager" }] : []),
            ...pool.map((p) => ({ name: p.name, tag: "Pool Approver (any 1)" })),
          ],
      pool_configured: pool.length > 0,
      locked: false,
    };
  }, [cfg, form.bypass_crm, advanceAmount, user, isEdit, editRecord]);

  const toggleMode = (mode) => {
    setForm((f) => ({
      ...f,
      transport_modes: f.transport_modes.includes(mode)
        ? f.transport_modes.filter((m) => m !== mode)
        : [...f.transport_modes, mode],
    }));
  };

  const submit = async () => {
    if (!form.travel_purpose) return toast.error("Purpose is required");
    if (form.travel_purpose === "Other" && !form.purpose_other.trim()) return toast.error("Please describe the purpose");
    if (!form.from_datetime || !form.to_datetime) return toast.error("Date From & To are required");
    if (days <= 0) return toast.error("Date To must be after Date From");
    if (!form.bypass_crm && form.client_ids.length === 0) return toast.error("Select at least one CRM Client or check Bypass CRM Verification");
    if (form.transport_modes.length === 0) return toast.error("Select at least one Transportation mode");
    if (advanceAmount <= 0) return toast.error("Advance amount must be greater than zero");

    const payload = {
      travel_purpose: form.travel_purpose === "Other" ? `Other — ${form.purpose_other.trim()}` : form.travel_purpose,
      purpose_other: form.purpose_other,
      from_datetime: form.from_datetime,
      to_datetime: form.to_datetime,
      from_date: form.from_datetime.slice(0, 10),
      to_date: form.to_datetime.slice(0, 10),
      destination: form.destination,
      // v109.9.110 — Multi-client support. Legacy `client_id` / `client_name`
      // are populated from the first selection + comma-joined names so
      // dashboards that expect a single value keep working.
      client_ids: form.client_ids,
      client_names: form.client_names,
      client_id: form.client_ids[0] || "",
      client_name: form.client_names.join(", "),
      bypass_crm: !!form.bypass_crm,
      transport_modes: form.transport_modes,
      kms: num(form.kms),
      fuel_cost: num(form.fuel_cost),
      toll_cost: num(form.toll_cost),
      public_transport_cost: num(form.public_transport_cost),
      lodging_cost: num(form.lodging_cost),
      food_cost: num(form.food_cost),
      advance_amount: advanceAmount,
      remark: form.remark,
      // v109.9.167 — When Master Admin edits an existing advance from the
      // All tab, preserve the ORIGINAL employee identity. Only overwrite
      // from the current session user for BRAND-NEW submissions. Backend
      // strips these fields on PATCH regardless, but keeping this correct
      // avoids surprises on any future non-generic PATCH surface.
      employee_name: isEdit ? (editRecord?.employee_name || editRecord?.created_by_name || "") : (user?.name || ""),
      employee_code: isEdit ? (editRecord?.employee_code || "") : (user?.employee_code || ""),
      // v109.9.165 — Persist the source training when raised via
      // "Apply Advance" from the Trainers module.
      trainer_assignment_id: form.trainer_assignment_id || "",
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/records/accounts_advances/${editRecord.id}`, payload);
        toast.success("Travel advance updated");
      } else {
        await api.post("/records/accounts_advances", payload);
        toast.success("Travel advance submitted for approval");
      }
      // v109.9.179 — Nudge related widgets to refresh.
      emitDataChange("travel:updated", { kind: "advance", id: editRecord?.id || null });
      emitDataChange("travel:advance:updated", { id: editRecord?.id || null });
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="travel-advance-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="heading">{isEdit ? "Edit Travel Advance" : "Raise Travel Advance"}</DialogTitle>
          <p className="text-[12px] text-slate-500">
            {isEdit
              ? "Update the trip details, cost breakdown and routing. Editing after any approver approves is not allowed."
              : "Fill in the trip details, transportation cost breakdown and select the client (or bypass with reason). Approvers are computed automatically from your amount + Master-Admin config."}
          </p>
        </DialogHeader>

        {/* v109.9.167 — Locked audit banner shown when editing an existing
            advance. Employee identity, advance code and the approval flow
            are audit-locked (backend enforces immutability too). MA can
            change trip / amount / breakdown but not identity or code. */}
        {isEdit && editRecord && (
          <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 p-3 mt-1" data-testid="tadv-locked-audit">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200 mb-2">
              <ShieldCheck size={12} /> Locked Audit Fields (read-only)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
              <div><span className="text-slate-500">Employee Name:</span> <b className="text-slate-800 dark:text-slate-100">{editRecord.employee_name || editRecord.created_by_name || "—"}</b></div>
              <div><span className="text-slate-500">Employee Code:</span> <b className="mono text-slate-800 dark:text-slate-100">{editRecord.employee_code || "—"}</b></div>
              <div><span className="text-slate-500">Advance Code:</span> <b className="mono text-indigo-700 dark:text-indigo-300">{editRecord.advance_number || editRecord.advance_code || "—"}</b></div>
              <div className="truncate"><span className="text-slate-500">Approval Flow:</span> <b className="text-slate-800 dark:text-slate-100">{(editRecord.approvals || []).map((a) => a.name || a.role).filter(Boolean).join(" → ") || (editRecord.approval_type || "—")}</b></div>
            </div>
          </div>
        )}

        {/* Unsettled warning */}
        {unsettled && unsettled.unsettled > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-[12.5px] flex items-start gap-2" data-testid="unsettled-warning">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-amber-900 dark:text-amber-200">
              <div className="font-semibold">
                You currently have an unsettled advance of ₹{unsettled.unsettled.toLocaleString("en-IN")}.
              </div>
              {unsettled.overdue_count > 0 && (
                <div className="mt-0.5">
                  {unsettled.overdue_count} advance(s) are older than 10 days — please submit settlement first.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4 py-2">
          {/* Purpose */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Purpose *
              </Label>
              <Select value={form.travel_purpose} onValueChange={(v) => setForm((f) => ({ ...f, travel_purpose: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="tadv-purpose"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {PURPOSE_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.travel_purpose === "Other" && (
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Describe *</Label>
                <Input
                  className="mt-1.5"
                  value={form.purpose_other}
                  onChange={(e) => setForm((f) => ({ ...f, purpose_other: e.target.value }))}
                  data-testid="tadv-purpose-other"
                  placeholder="Explain the purpose of travel"
                />
              </div>
            )}
          </div>

          {/* Dates + auto-days */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Date From (with time) *</Label>
              <Input
                type="datetime-local" className="mt-1.5"
                value={form.from_datetime}
                onChange={(e) => setForm((f) => ({ ...f, from_datetime: e.target.value }))}
                data-testid="tadv-from"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Date To (with time) *</Label>
              <Input
                type="datetime-local" className="mt-1.5"
                value={form.to_datetime}
                onChange={(e) => setForm((f) => ({ ...f, to_datetime: e.target.value }))}
                data-testid="tadv-to"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Days (auto)</Label>
              <div className="mt-1.5 h-10 rounded-md border border-slate-300 bg-slate-50 dark:bg-slate-900 dark:border-slate-700 px-3 flex items-center text-[13px] font-semibold text-slate-800 dark:text-slate-200" data-testid="tadv-days">
                {days || "—"} day(s)
              </div>
            </div>
          </div>

          {/* Destination */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Destination</Label>
            <Input
              className="mt-1.5"
              placeholder="City / Region"
              value={form.destination}
              onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
              data-testid="tadv-destination"
            />
          </div>

          {/* Client picker + bypass */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2 bg-slate-50/60 dark:bg-slate-900/40">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-700 dark:text-slate-200">
              <Users size={14} /> CRM Client
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  Select existing client(s) — type to search, add multiple
                </Label>
                <ClientMultiTypeahead
                  clients={clients}
                  valueIds={form.client_ids}
                  valueNames={form.client_names}
                  onChange={(ids, names) => setForm((f) => ({ ...f, client_ids: ids, client_names: names, bypass_crm: false }))}
                  disabled={form.bypass_crm}
                />
              </div>
              <div>
                <label className="flex items-start gap-2 mt-6 cursor-pointer" data-testid="tadv-bypass-wrap">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.bypass_crm}
                    onChange={(e) => setForm((f) => ({ ...f, bypass_crm: e.target.checked, client_ids: e.target.checked ? [] : f.client_ids, client_names: e.target.checked ? [] : f.client_names }))}
                    data-testid="tadv-bypass"
                  />
                  <span className="text-[12px] font-semibold text-rose-800 dark:text-rose-300">
                    Bypass CRM verification
                  </span>
                </label>
              </div>
            </div>
            {form.bypass_crm && (
              <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 p-2.5 text-[12px] text-rose-800 dark:text-rose-200 flex items-start gap-2" data-testid="tadv-bypass-warning">
                <AlertTriangle size={14} className="mt-0.5" />
                <span>
                  Bypassing CRM will route this request through the full <b>4-approver</b> pool for
                  additional scrutiny.
                </span>
              </div>
            )}
          </div>

          {/* Transport */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-3 bg-slate-50/60 dark:bg-slate-900/40">
            <div className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">Transportation</div>
            <div className="flex flex-wrap gap-2" data-testid="tadv-modes">
              {TRANSPORT_MODES.map((m) => {
                const on = form.transport_modes.includes(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => toggleMode(m.key)}
                    className={`text-[12px] px-3 py-1.5 rounded-full border transition-all ${
                      on
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                        : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-indigo-400"
                    }`}
                    data-testid={`tadv-mode-${m.key.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>

            {hasOwn && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <NumField label="KMS travelled" value={form.kms} onChange={(v) => setForm((f) => ({ ...f, kms: v }))} testid="tadv-kms" />
                <NumField label="Fuel Cost (₹)" value={form.fuel_cost} onChange={(v) => setForm((f) => ({ ...f, fuel_cost: v }))} testid="tadv-fuel" />
                <NumField label="Toll Cost (₹)" value={form.toll_cost} onChange={(v) => setForm((f) => ({ ...f, toll_cost: v }))} testid="tadv-toll" />
              </div>
            )}
            {hasPublic && (
              <NumField label="Public Transport Cost (₹)" value={form.public_transport_cost} onChange={(v) => setForm((f) => ({ ...f, public_transport_cost: v }))} testid="tadv-public" />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NumField label="Lodging Cost (₹)" value={form.lodging_cost} onChange={(v) => setForm((f) => ({ ...f, lodging_cost: v }))} testid="tadv-lodging" />
              <NumField label="Food Cost (₹)" value={form.food_cost} onChange={(v) => setForm((f) => ({ ...f, food_cost: v }))} testid="tadv-food" />
            </div>
          </div>

          {/* Totals + Advance requested */}
          <div className="rounded-lg border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/30 p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5">
                <Calculator size={12} /> Breakdown Total
              </div>
              <div className="text-2xl font-bold text-indigo-800 dark:text-indigo-300 mt-0.5" data-testid="tadv-breakdown-total">
                ₹ {totalBreakdown.toLocaleString("en-IN")}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Fuel + Toll + Public + Lodging + Food (per {days || 0} day(s))
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Advance Requested (₹) *
              </Label>
              <Input
                type="number" min="0" step="1"
                placeholder={String(totalBreakdown)}
                value={form.advance_amount}
                onChange={(e) => setForm((f) => ({ ...f, advance_amount: e.target.value }))}
                className="mt-1.5 text-[15px] font-bold"
                data-testid="tadv-amount"
              />
            </div>
          </div>

          {/* Routing preview */}
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/20 p-3" data-testid="tadv-routing-preview">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-emerald-900 dark:text-emerald-200">
              <ShieldCheck size={14} />
              Approval Flow ·
              <Badge className={routing.type === "4-tier" ? "bg-rose-100 text-rose-800 border-rose-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                {routing.type}
              </Badge>
              <span className="text-slate-500 font-normal">
                (threshold ₹{Number(routing.threshold).toLocaleString("en-IN")})
              </span>
            </div>
            {!routing.pool_configured && (
              <div className="mt-1.5 text-[11.5px] text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                <Info size={12} /> Master Admin has not yet configured the 4-approver pool.
                Your request will still be created but may need manual routing.
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {routing.approvers.length === 0 ? (
                <span className="text-[12px] text-slate-500">No approvers configured yet.</span>
              ) : (
                routing.approvers.map((a, i) => (
                  <Badge key={i} variant="outline" className="text-[11px] bg-white dark:bg-slate-900">
                    {i + 1}. {a.name} <span className="opacity-60 ml-1">· {a.tag}</span>
                  </Badge>
                ))
              )}
            </div>
          </div>

          {/* Remark */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Remark (optional)</Label>
            <Textarea rows={2} value={form.remark} onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))} className="mt-1.5" data-testid="tadv-remark" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={saving} data-testid="tadv-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="tadv-submit"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <Input
        type="number" min="0" step="1"
        placeholder="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5"
        data-testid={testid}
      />
    </div>
  );
}

/**
 * v109.9.110 — ClientMultiTypeahead.
 * Type-to-search dropdown that supports adding *multiple* clients.
 * Selected clients appear as chips at the top; typing filters the list
 * to the remaining clients only. Clicking a chip's × removes it.
 *
 * Plain-Input based (rather than shadcn Popover + Command) because those
 * get z-index conflicts inside a Dialog on Safari and can occasionally
 * trap focus. This variant is bulletproof.
 */
function ClientMultiTypeahead({ clients, valueIds, valueNames, onChange, disabled }) {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const remaining = clients.filter((c) => !valueIds.includes(c.id));
    if (!term) return remaining.slice(0, 40);
    return remaining
      .filter((c) => (c.client_name || "").toLowerCase().includes(term)
                  || (c.district || "").toLowerCase().includes(term)
                  || (c.state || "").toLowerCase().includes(term)
                  || (c.pincode || "").includes(term))
      .slice(0, 40);
  }, [clients, q, valueIds]);

  const add = (c) => {
    const ids = [...valueIds, c.id];
    const names = [...valueNames, c.client_name];
    onChange(ids, names);
    setQ("");
  };
  const remove = (idx) => {
    const ids = valueIds.filter((_, i) => i !== idx);
    const names = valueNames.filter((_, i) => i !== idx);
    onChange(ids, names);
  };

  return (
    <div className="mt-1.5 space-y-2">
      {valueIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="tadv-client-chips">
          {valueNames.map((n, i) => (
            <span
              key={valueIds[i]}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 text-[11.5px] font-semibold text-emerald-900 dark:text-emerald-200"
              data-testid={`tadv-client-chip-${valueIds[i]}`}
            >
              {n}
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                className="text-emerald-800 hover:text-rose-700 leading-none"
                aria-label={`Remove ${n}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Input
          type="text"
          placeholder={clients.length ? "Type school / client name…" : "No CRM clients yet"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          disabled={disabled || clients.length === 0}
          data-testid="tadv-client"
        />
        {focused && !disabled && filtered.length > 0 && (
          <div
            className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
            data-testid="tadv-client-suggestions"
          >
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); add(c); }}
                className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                data-testid={`tadv-client-opt-${c.id}`}
              >
                <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100">{c.client_name}</div>
                <div className="text-[10.5px] text-slate-500 flex flex-wrap gap-x-2">
                  {c.district ? <span>{c.district}</span> : null}
                  {c.state ? <span>· {c.state}</span> : null}
                  {c.pincode ? <span>· {c.pincode}</span> : null}
                </div>
              </button>
            ))}
          </div>
        )}
        {focused && !disabled && q.trim() && filtered.length === 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-3 text-[12px] text-slate-500 text-center">
            No client matches &quot;{q}&quot;
          </div>
        )}
      </div>
    </div>
  );
}
